import {
  UserWebmap,
  UserWebmapCluster,
  UserWebmapGebiet,
} from "../context/authenticationContext";
import { HEADERS, WebmapSelf } from "./portal";

import axios from "axios";
import { AccountSelf } from "../types/account";
import { Hierarchy, hierarchyFromNumber } from "../types/hierarchy";

const env = import.meta.env.MODE;
const hostDev = import.meta.env.VITE_HOST_DEV;

/**
 *
 * @param webmaps A list of webmaps the current user is assigned to as returned from the Portal API
 * @param user The current user
 * @returns a list of webmaps that includes clusters and gebiete a user has access to
 */
export const getWebmapDetails = (
  webmaps: WebmapSelf[],
  user: AccountSelf
): Promise<UserWebmap[]> => {
  // We will fetch a user's Gebiete per webmap asynchronously
  // so we need to be able to track which gebiete belong to which webmap
  type UserWebMapGebietWithID = {
    type: "gebiet";
    webmapID: number;
    gebiete: UserWebmapGebiet[];
    gebieteTotalCount: number;
  };

  type UserWebMapClusterWithID = {
    type: "cluster";
    webmapID: number;
    cluster: UserWebmapCluster[];
    clusterTotalCount: number;
  };

  const fetchGebietePromises: Promise<UserWebMapGebietWithID>[] = [];
  const fetchClusterPromises: Promise<UserWebMapClusterWithID>[] = [];
  const editedWebmaps: UserWebmap[] = [];

  for (let i = 0; i < webmaps.length; ++i) {
    const userWebmap = webmaps[i];
    const {
      backend_url_prod,
      backend_port_dev,
      frontend_url_prod,
      frontend_port_dev,
      ...userWebmapRest
    }: WebmapSelf = userWebmap;

    // we determine whether to use the dev or prod URL returned from the server
    const userWebmapCast: any = userWebmapRest;
    userWebmapCast.backend_url =
      env === "development"
        ? `${hostDev}:${backend_port_dev}/api/v1`
        : backend_url_prod;
    userWebmapCast.frontend_url =
      env === "development"
        ? `${hostDev}:${frontend_port_dev}`
        : frontend_url_prod;

    userWebmapCast.gebiete = [];
    userWebmapCast.cluster = [];
    if (
      (env === "development" && backend_port_dev) ||
      (env === "production" && backend_url_prod)
    ) {
      // if there is a backend URL, we use it to fetch the Gebiete and cluster for the associated
      // webmap that the user has access to.
      const fetchGebiet = new Promise<UserWebMapGebietWithID>(
        (resolve, reject) => {
          let url = userWebmapCast.backend_url + "/gebiete/ars/";
          if (
            userWebmap.id === user.last_webmap &&
            hierarchyFromNumber(user.last_filter as 1 | 2) === Hierarchy.ARS
          ) {
            url = url + `?includeID=${user.last_ars}`;
          }
          axios
            .get(url, {
              withCredentials: true,
              headers: HEADERS,
            })
            .then((res) => {
              const gebieteTotalCount = Number(res.headers["x-total-count"]);
              const resolveVal: UserWebMapGebietWithID = {
                type: "gebiet",
                webmapID: userWebmapCast.id as number,
                gebiete: res.data,
                gebieteTotalCount,
              };
              resolve(resolveVal);
            })
            .catch(reject);
        }
      );

      fetchGebietePromises.push(fetchGebiet);

      const fetchCluster = new Promise<UserWebMapClusterWithID>(
        (resolve, reject) => {
          let url = userWebmapCast.backend_url + "/gebiete/nvt/";
          if (
            userWebmap.id === user.last_webmap &&
            hierarchyFromNumber(user.last_filter as 1 | 2) === Hierarchy.NTS
          ) {
            url = url + `?includeID=${user.last_nts}`;
          }
          axios
            .get(url, {
              withCredentials: true,
              headers: HEADERS,
            })
            .then((res) => {
              const clusterTotalCount = Number(res.headers["x-total-count"]);
              const resolveVal: UserWebMapClusterWithID = {
                type: "cluster",
                webmapID: userWebmapCast.id as number,
                cluster: res.data,
                clusterTotalCount,
              };
              resolve(resolveVal);
            })
            .catch(reject);
        }
      );

      fetchClusterPromises.push(fetchCluster);
    }
    editedWebmaps.push(userWebmapCast);
  }

  return Promise.all([...fetchGebietePromises, ...fetchClusterPromises]).then(
    (gebiete: (UserWebMapGebietWithID | UserWebMapClusterWithID)[]) => {
      // This is the tricky part: we fetch the gebiete asynchronously and
      // associate them back to the correct webmap object by the ID that we
      // passed into the promises' resolve values earlier
      for (let i = 0; i < gebiete.length; ++i) {
        for (let j = 0; j < editedWebmaps.length; ++j) {
          const gebieteWithId = gebiete[i];
          const webmap = editedWebmaps[j];
          switch (gebieteWithId.type) {
            case "gebiet":
              if (gebieteWithId.webmapID === webmap.id) {
                webmap.gebiete = gebieteWithId.gebiete;
                webmap.gebieteTotalCount = gebieteWithId.gebieteTotalCount;
              }
              break;
            case "cluster":
              if (gebieteWithId.webmapID === webmap.id) {
                webmap.cluster = gebieteWithId.cluster;
                webmap.clusterTotalCount = gebieteWithId.clusterTotalCount;
              }
              break;
          }
        }
      }

      return editedWebmaps;
    }
  );
};
